/**
 * @file Contract for the held-push record at
 * `<artifactPath>/heldPushes/{userId}__{fileUid}`: the server's own account of a
 * desktop-agent save it refused. Written and deleted only by the Admin SDK —
 * `checkArtifactWriteAccess` writes it at the instant it refuses,
 * `commitAttachmentVersion` deletes it when a save for the same user and file
 * lands, and the attachments panel reads it and groups the reader's own records
 * by `reason` into one alert each.
 * A subcollection rather than a field on the artifact, because the client
 * `setMerge`s the artifact's attachment map on autosave and would clobber
 * server-owned state there. Clients may read and delete, never create or update.
 */

import type { TimeType } from './System.types';

/**
 * Why the push was refused. It names the precondition the gate evaluated, not
 * the sentence `checkPermission` returned. `formVersionNotDraft` clears on its
 * own when the form returns to draft; `noWriteAccess` clears only when the user
 * gains write access. A form version whose `state` is unreadable also reads as
 * `formVersionNotDraft`, because the agent receives the same wire sentence for
 * it and the browser must agree with the tray. A frozen version the user also
 * has no grant on reads as `formVersionNotDraft` too — the freeze gate
 * short-circuits before either grant lane, so the refusal cannot know the rights
 * would have failed as well; the next refusal after the version reopens says
 * `noWriteAccess`.
 */
export type HeldAttachmentPushReason = 'formVersionNotDraft' | 'noWriteAccess';

/** One held push. The document id is `heldAttachmentPushId(userId, fileUid)`. */
export type HeldAttachmentPush = {
  /** Whose agent pushed. Repeated from the id so the reader never splits it. */
  userId: string;
  /** Which attachment the push belongs to; the reader joins it to the artifact's map. */
  fileUid: string;
  reason: HeldAttachmentPushReason;
  /** The latest refusal. Every refusal overwrites the whole document. */
  attemptedAt: TimeType;
};

/**
 * Subcollection name. Deliberately outside `AR_TABLE_COLLECTION_PREFIX`, so
 * the `firestore.rules` wildcard that grants clients row writes never reaches it.
 */
export const HELD_PUSHES_COLLECTION = 'heldPushes';

/**
 * Deterministic id, so an agent's retry ladder collapses into one document.
 * Neither a uid nor a uuid contains `__`, so the two halves cannot blur.
 */
export const heldAttachmentPushId = (userId: string, fileUid: string): string =>
  `${userId}__${fileUid}`;
